import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotificationStack,
  NotificationType,
  Notification,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import container from "@src/container";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

import "./delete-modal.scss";

export const BulkRemoveUserModal = ({
  onCancel,
  show,
  className,
  deleteList,
  onRowRemoved,
  classroomName,
  classroomId,
  userLabel,
  isMultiProductClassroom,
  isProfessionalLearning,
}) => {
  const { singular, plural } = userLabel;
  const [notifications, setNotifications] = useState();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [showWarning, setShowWarning] = useState(true);

  const { classroomService, userService } = container;

  const handleClose = () => {
    onCancel();
  };

  const onCloseNotification = (id) => {
    setNotifications((n) => n.filter((notification) => notification.id !== id));
  };

  const showNotification = (type, message, icon) => {
    const notification = { type, message, icon, id: crypto.randomUUID() };
    setNotifications((n) => {
      const notificationsUpdated = n ?? [];
      return [...notificationsUpdated, notification];
    });
  };

  const { handleSubmit, reset } = useForm({
    defaultValues: { ...deleteList },
  });

  const getContent = (deleteList) => {
    return deleteList.length == 1 ? (
      <div className="delete-content">
        Would you like to remove
        <b>
          {" "}
          {deleteList[0].firstName} {deleteList[0].lastName}{" "}
        </b>
        from <b>{classroomName}</b>?
      </div>
    ) : (
      <div className="delete-content">
        Would you like to remove
        <b> {deleteList.length} </b>
        {getLearnerTerm({ lowercase: true, plural: true })} from{" "}
        <b>{classroomName}</b>?
      </div>
    );
  };

  async function getStudentsToRemove(values) {
    if (isProfessionalLearning) {
      return [values, []];
    }

    const students = await Promise.all(
      values.map(async (student) => {
        return {
          ...student,
          classrooms: await userService.getUserClassrooms(student["userId"]),
        };
      })
    );

    const [allowRemove, blockRemove] = students.reduce(
      ([p, f], e) =>
        e.classrooms.length > 1 ? [[...p, e], f] : [p, [...f, e]],
      [[], []]
    );

    return [allowRemove, blockRemove];
  }

  async function submitForm(values, e) {
    try {
      const [allowRemove, blockRemove] = await getStudentsToRemove(values);

      if (blockRemove.length > 0) {
        showNotification(
          NotificationType.error,
          blockRemove.length > 1 ? (
            <div>
              <b>{blockRemove.length}</b>{" "}
              {getLearnerTerm({ lowercase: true, plural: true })} only enrolled
              in <b>{classroomName}</b> could not be removed.
            </div>
          ) : (
            <div>
              <b>
                {blockRemove[0].firstName} {blockRemove[0].lastName}
              </b>{" "}
              could not be removed while only enrolled in <b>{classroomName}</b>
              .
            </div>
          )
        );
      }

      if (allowRemove.length > 0) {
        const traineeIds = allowRemove.map((x) => x["userId"]);
        await classroomService.bulkRemoveTrainee(classroomId, {
          traineeIds: traineeIds,
        });
        onRowRemoved();
        showNotification(
          NotificationType.success,
          allowRemove.length > 1 ? (
            <div>
              <b>{allowRemove.length}</b>{" "}
              {getLearnerTerm({ lowercase: true, plural: true })} have been
              removed.
            </div>
          ) : (
            <div>
              <b>
                {allowRemove[0].firstName} {allowRemove[0].lastName}
              </b>{" "}
              has been removed.
            </div>
          )
        );
      }
    } catch (error) {
      console.log(error);
      console.trace();
      showNotification(NotificationType.error, "Something went wrong");
    } finally {
      handleClose();
      setIsAlertVisible(true);
      setTimeout(() => {
        setIsAlertVisible(false);
        setNotifications();
      }, 10000);
    }
  }

  const onClick = (_, e) => {
    handleSubmit(submitForm)(e);
  };

  useEffect(() => {
    reset(deleteList);
  }, [deleteList]);

  return (
    <div className={clsx("delete-modal-container", className)}>
      <Modal modalClassName={"delete-modal"} open={show} onClose={handleClose}>
        <ModalHeader
          className="red-icon"
          icon={["fa-light", "user-xmark"]}
          title={`Remove ${deleteList.length > 1 ? plural : singular}`}
        ></ModalHeader>
        <ModalBody className="delete-body">
          {showWarning && !isProfessionalLearning && (
            <div style={{ paddingBottom: "16px" }}>
              <Notification
                type={NotificationType.error}
                closable
                onClose={() => setShowWarning(false)}
              >
                <div>
                  {isMultiProductClassroom && (
                    <>
                      This will remove the{" "}
                      {deleteList.length > 1
                        ? plural.toLowerCase()
                        : singular.toLowerCase()}{" "}
                      from all products.{" "}
                    </>
                  )}
                  A {getLearnerTerm({ lowercase: true })} must be enrolled in at
                  least one classroom.
                </div>
              </Notification>
            </div>
          )}
          {getContent(deleteList)}
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose}>No, Cancel</Button>
          <Button destructive onClick={onClick}>
            Yes, Remove
          </Button>
        </ModalFooter>
      </Modal>
      <div className="success-notification">
        {isAlertVisible && notifications?.length > 0 && (
          <NotificationStack
            notifications={notifications}
            onClose={onCloseNotification}
            closable
          ></NotificationStack>
        )}
      </div>
    </div>
  );
};
